import { useRef, useState, type DragEvent, type ReactNode } from "react";

/** Does a file match an <input accept> list (".pdf,image/*,application/pdf")? Empty accept = anything. */
export function acceptMatch(f: File, accept?: string): boolean {
  if (!accept) return true;
  const name = f.name.toLowerCase(), type = (f.type || "").toLowerCase();
  return accept.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean).some((a) =>
    a.startsWith(".") ? name.endsWith(a) : a.endsWith("/*") ? type.startsWith(a.slice(0, -1)) : type === a);
}

/** Split dropped files into ones this spot takes and a plain-English error for the rest. */
export function sortDropped(files: File[], accept: string | undefined, kind: string, multiple: boolean): { ok: File[]; error: string | null } {
  const good = files.filter((f) => acceptMatch(f, accept)), bad = files.filter((f) => !acceptMatch(f, accept));
  const msgs: string[] = [];
  if (bad.length) msgs.push(`${bad.length === 1 ? `“${bad[0]!.name}” isn't` : `${bad.length} files aren't`} ${kind}, so ${bad.length === 1 ? "it wasn't" : "they weren't"} added.`);
  let ok = good;
  if (!multiple && good.length > 1) { ok = good.slice(0, 1); msgs.push(`This spot takes one file at a time; used “${good[0]!.name}”.`); }
  return { ok, error: msgs.length ? msgs.join(" ") : null };
}

const hasFiles = (e: DragEvent) => [...(e.dataTransfer?.types ?? [])].includes("Files");

/**
 * Wrap any upload spot: drag files over it → highlighted "Drop files here"; drop → onFiles(files that match accept).
 * Tap/browse buttons inside keep working. Disabled = drops ignored while busy (wrapper stays mounted so inputs keep focus). Permission: just don't render it.
 */
export function DropFiles({ accept, kind = "a supported file", multiple = true, disabled, onFiles, label = "Drop files here", className = "", children, testId }: {
  accept?: string; kind?: string; multiple?: boolean; disabled?: boolean; onFiles: (files: File[]) => void; label?: string; className?: string; children: ReactNode; testId?: string;
}) {
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const depth = useRef(0);
  return (
    <div className={`drop-target${over ? " drop-over" : ""} ${className}`} data-testid={testId} data-drop="1"
      onDragEnter={(e) => { if (!hasFiles(e) || disabled) return; e.preventDefault(); depth.current++; setOver(true); }}
      onDragOver={(e) => { if (!hasFiles(e)) return; e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = disabled ? "none" : "copy"; }}
      onDragLeave={(e) => { if (!hasFiles(e)) return; depth.current = Math.max(0, depth.current - 1); if (!depth.current) setOver(false); }}
      onDrop={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault(); e.stopPropagation(); depth.current = 0; setOver(false);
        if (disabled) { setError("Still busy with the last upload. Try again in a moment."); return; }
        const { ok, error } = sortDropped([...e.dataTransfer.files], accept, kind, multiple);
        setError(error);
        if (ok.length) onFiles(ok);
      }}>
      {children}
      {over && <div className="drop-overlay" aria-hidden="true"><span>⬇ {label}</span></div>}
      {error && <div className="error drop-error" role="alert">{error} <button type="button" className="linkish" onClick={() => setError(null)}>OK</button></div>}
    </div>
  );
}

/** Stop the browser from opening a file dropped anywhere that isn't an upload spot (e.g. on the plan viewer). */
export function guardWindowDrops(): void {
  const isFiles = (e: globalThis.DragEvent) => [...(e.dataTransfer?.types ?? [])].includes("Files");
  window.addEventListener("dragover", (e) => { if (isFiles(e)) { e.preventDefault(); if (e.dataTransfer && !(e.target as Element | null)?.closest?.("[data-drop]")) e.dataTransfer.dropEffect = "none"; } });
  window.addEventListener("drop", (e) => { if (isFiles(e)) e.preventDefault(); });
}
